import React from "react";
import { motion } from "framer-motion";
import { Sun, User, Sparkles } from "lucide-react";
import TypingDots from "./TypingDots";
import RichText from "./RichText";
import VenueCard from "./VenueCard";
import { parseMessage } from "@/lib/parseMessage";
import { pickImageForContent } from "@/lib/images";

function segmentKey(seg, idx) {
  if (seg.type === "venue") return `venue-${seg.data.name || idx}`;
  const snippet = (seg.text || "").slice(0, 24);
  return `text-${idx}-${snippet}`;
}

function BotContent({ segments }) {
  return (
    <div className="flex flex-col gap-1">
      {segments.map((seg, idx) => {
        const key = segmentKey(seg, idx);
        if (seg.type === "venue") {
          return <VenueCard key={key} data={seg.data} />;
        }
        return (
          <div
            key={key}
            className="px-4 py-3 text-[15px] leading-relaxed font-body whitespace-pre-wrap bg-[#f7f1e7] text-[#242f44] rounded-br-2xl rounded-tr-2xl rounded-tl-2xl rounded-bl-md border border-[#eee5d1]"
          >
            <RichText text={seg.text} />
          </div>
        );
      })}
    </div>
  );
}

function UserBubbleBody({ content }) {
  return (
    <div className="px-4 py-3 text-[15px] leading-relaxed font-body whitespace-pre-wrap bg-[#242f44] text-white rounded-bl-2xl rounded-tl-2xl rounded-tr-2xl rounded-br-md">
      {content}
    </div>
  );
}

function BotBubbleBody({ content, segments }) {
  if (!content) {
    return (
      <div className="px-4 py-3 bg-[#f7f1e7] border border-[#eee5d1] rounded-br-2xl rounded-tr-2xl rounded-tl-2xl rounded-bl-md">
        <TypingDots />
      </div>
    );
  }
  return <BotContent segments={segments} />;
}

function ImageCard({ image }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="mt-2 rounded-xl overflow-hidden border border-[#e4e9f0] max-w-[300px] bg-white"
      data-testid="bot-image-card"
    >
      <img
        src={image.src}
        alt={image.caption}
        className="w-full h-44 object-cover"
        loading="lazy"
      />
      <div className="px-3 py-2 text-[12px] text-[#5b6b83] font-body flex items-center gap-1.5">
        <Sparkles size={12} className="text-[#faa200]" />
        {image.caption}
      </div>
    </motion.div>
  );
}

export default function Bubble({ msg }) {
  const isUser = msg.role === "user";

  const segments = isUser ? null : parseMessage(msg.content);
  const hasVenues = !isUser && segments && segments.some((s) => s.type === "venue");
  const plainTextContent = segments
    ? segments.filter((s) => s.type === "text").map((s) => s.text).join(" ")
    : msg.content;

  let image = null;
  if (!isUser && !hasVenues && plainTextContent) {
    image = pickImageForContent(plainTextContent);
  }
  const showImage = Boolean(image) && plainTextContent && plainTextContent.length > 120;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: "easeOut" }}
      className={`flex gap-2.5 ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={isUser ? "user-message" : "bot-message"}
    >
      {!isUser && (
        <div className="mt-1 h-9 w-9 rounded-lg bg-[#faa200] flex items-center justify-center text-white shadow-sm shrink-0">
          <Sun size={18} strokeWidth={2.2} />
        </div>
      )}

      <div className={`max-w-[80%] flex flex-col ${isUser ? "items-end" : "items-start"}`}>
        {isUser ? (
          <UserBubbleBody content={msg.content} />
        ) : (
          <BotBubbleBody content={msg.content} segments={segments} />
        )}

        {showImage && <ImageCard image={image} />}
      </div>

      {isUser && (
        <div className="mt-1 h-9 w-9 rounded-lg bg-white border border-[#242f44]/15 flex items-center justify-center text-[#242f44] shrink-0">
          <User size={16} />
        </div>
      )}
    </motion.div>
  );
}
